import * as z$1 from "zod";
import { z } from "zod";

//#region src/freeze.ts
const g = globalThis;
const FROZEN = Symbol.for("@spark/deepFreeze.frozen");
const RAW = Symbol.for("@spark/deepFreeze.raw");
const proxyCache = g[Symbol.for("@spark/deepFreeze.cache")] ??= /* @__PURE__ */ new WeakMap();
const MUTATOR_PATTERNS = /* @__PURE__ */ new Map([
	[Map.prototype, /^(?:set|delete|clear)$/],
	[Set.prototype, /^(?:add|delete|clear)$/],
	[Date.prototype, /^set/],
	[URLSearchParams.prototype, /^(?:set|append|delete|sort)$/]
]);
function throwFrozen(op, key) {
	throw new TypeError(`Frozen object: cannot ${op}${key !== void 0 ? ` '${String(key)}'` : ""}`);
}
function isFrozen(value) {
	if (value === null || typeof value !== "object" && typeof value !== "function") return true;
	return Object.isFrozen(value) || value[FROZEN] === true;
}
function deepFreeze(value) {
	if (value === null || typeof value !== "object" && typeof value !== "function") return value;
	const target = value;
	if (isFrozen(target)) return value;
	const cached = proxyCache.get(target);
	if (cached) return cached;
	const proto = Reflect.getPrototypeOf(target);
	const isGeneric = proto === Object.prototype || proto === null || Array.isArray(target);
	const proxy = new Proxy(target, {
		set: (_t, k) => throwFrozen("set", k),
		defineProperty: (_t, k) => throwFrozen("defineProperty", k),
		deleteProperty: (_t, k) => throwFrozen("delete", k),
		setPrototypeOf: () => throwFrozen("setPrototypeOf"),
		get(t, k, receiver) {
			if (k === FROZEN) return true;
			if (k === RAW) return t;
			if (k === "constructor" || k === "__proto__") return Reflect.get(t, k, t);
			const desc = Reflect.getOwnPropertyDescriptor(t, k);
			if (desc && "value" in desc && !desc.configurable && !desc.writable) return desc.value;
			const v = Reflect.get(t, k, isGeneric ? receiver : t);
			if (typeof v === "function" && !isGeneric) {
				if (MUTATOR_PATTERNS.get(proto)?.test(String(k))) return () => throwFrozen(`call mutating method`, k);
				const bound = v.bind(t);
				return (...args) => deepFreeze(bound(...args));
			}
			return deepFreeze(v);
		}
	});
	proxyCache.set(target, proxy);
	proxyCache.set(proxy, proxy);
	return proxy;
}

//#endregion
//#region src/json.schemas.ts
/**
* JSON encodable scalar value.
* This includes strings, numbers, booleans, and null.
* @example 'hello'
* @example 123
* @example true
* @example null
*/
const JsonScalarSchema = z$1.union([
	z$1.string(),
	z$1.number(),
	z$1.boolean(),
	z$1.null()
]).meta({
	id: "json-scalar",
	title: "JSON Scalar",
	description: "A JSON primitive value, which can be a string, number, boolean, or null.",
	examples: [
		"hello",
		123,
		true,
		null
	]
});
/**
* JSON encodable scalar value.
* This includes strings, numbers, and booleans.
* @example 'hello'
* @example 123
* @example true
*/
const JsonNonNullScalarSchema = z$1.union([
	z$1.string(),
	z$1.number(),
	z$1.boolean()
]).meta({
	id: "json-scalar-non-null",
	title: "JSON Scalar Non-Null",
	description: "A JSON primitive value, which can be a string, number, or boolean.",
	examples: [
		"hello",
		123,
		true
	]
});
/**
* JSON encodable value.
* This includes JSON scalars, arrays of JSON values, and objects with string keys and JSON values.
* @example { "key": "value", "number": 123, "array": [1, 2, 3], "nested": { "bool": true } }
*/
const JsonSchema = z$1.lazy(() => z$1.union([
	JsonScalarSchema,
	z$1.array(JsonSchema),
	z$1.record(z$1.string(), JsonSchema)
])).meta({
	id: "json",
	title: "JSON value",
	description: "A JSON value, which can be a string, number, boolean, null, array, or object.",
	examples: [[
		"hello",
		123,
		true,
		null,
		{
			key: "value",
			number: 123,
			array: [
				1,
				2,
				3
			],
			nested: { bool: true }
		},
		[
			"hello",
			123,
			true,
			null,
			{
				key: "value",
				number: 123,
				array: [
					1,
					2,
					3
				],
				nested: { bool: true }
			}
		]
	]]
});
/**
* JSON encodable value.
* This includes JSON scalars, arrays of JSON values, and objects with string keys and JSON values.
* @example { "key": "value", "number": 123, "array": [1, 2, 3], "nested": { "bool": true } }
*/
const JsonNonNullSchema = z$1.union([
	JsonNonNullScalarSchema,
	z$1.array(JsonSchema),
	z$1.record(z$1.string(), JsonSchema)
]).meta({
	id: "json-non-null",
	title: "JSON Non-Null value",
	description: "A JSON value, which can be a string, number, boolean, array, or object, but not null.",
	examples: [
		"hello",
		123,
		true,
		{
			key: "value",
			number: 123,
			array: [
				1,
				2,
				3
			],
			nested: { bool: true }
		},
		[
			"hello",
			123,
			true,
			null,
			{
				key: "value",
				number: 123,
				array: [
					1,
					2,
					3
				],
				nested: { bool: true }
			}
		]
	]
});
/**
* An object with string keys and JSON values.
* @example { "key": "value", "number": 123, "array": [1, 2, 3], "nested": { "bool": true } }
*/
const JsonObjectSchema = z$1.record(z$1.string(), JsonSchema).meta({
	id: "json-object",
	title: "JSON object",
	description: "A JSON object, which is a collection of key-value pairs where the keys are strings and the values are JSON values.",
	examples: [{
		key: "value",
		number: 123,
		array: [
			1,
			2,
			3
		],
		nested: { bool: true }
	}]
});

//#endregion
//#region src/models/asset.schema.ts
const ASSET_TYPES = [
	"image",
	"video",
	"audio",
	"document"
];
z.enum(ASSET_TYPES);
const ASSET_EXTENSIONS = deepFreeze({
	image: [
		"jpg",
		"jpeg",
		"png",
		"gif",
		"svg"
	],
	video: [
		"mp4",
		"webm",
		"ogg"
	],
	audio: [
		"mp3",
		"wav",
		"ogg"
	],
	document: [
		"pdf",
		"doc",
		"docx",
		"xls",
		"xlsx",
		"ppt",
		"pptx"
	]
});
const AssetUrlSchema = (kind) => z.url().refine((url) => {
	if (url.includes("bynder.com")) return true;
	const extension = url.split(".").pop()?.toLowerCase();
	const valid = ASSET_EXTENSIONS[kind];
	return extension ? valid.includes(extension) : false;
}, `URL must have a valid ${kind} extension`);
const bynderFile = z.object({
	url: z.url(),
	width: z.number().nullish(),
	height: z.number().nullish(),
	fileSize: z.number().nullish()
});
/**
* Bynder database ID is a mangled UUIDv4 missing the last '-'.
* @example "F8CB9223-15C3-4FCD-B7F176C2ABEE10FD"
*/
const bynderDatabaseId = z.string().regex(/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-?[0-9A-F]{12}$/i, { message: "Bynder database ID must be a mangled UUIDv4 missing the last '-' character." }).meta({
	id: "bynder-database-id",
	title: "Bynder Database ID",
	description: "Bynder database ID is a mangled UUIDv4 missing the last '-'.",
	examples: ["F8CB9223-15C3-4FCD-B7F176C2ABEE10FD"]
});
/**
* Bynder returns an empty string for assets it has no preview or stream for,
* such as video. Accept it so those assets validate.
*/
const bynderUrl = z.union([z.url(), z.literal("")]);
const bynderDataSchema = z.object({
	createdAt: z.string().optional(),
	databaseId: bynderDatabaseId,
	derivatives: JsonObjectSchema.optional(),
	description: z.string().nullish(),
	extensions: z.array(z.string().max(10)).optional(),
	files: z.record(z.string(), bynderFile.optional()),
	id: z.string().max(256),
	metaproperties: JsonObjectSchema.optional(),
	name: z.string().max(256),
	originalUrl: z.url().nullable().optional(),
	previewUrls: z.array(bynderUrl).nullable().optional(),
	publishedAt: z.string().optional(),
	streamingLinks: z.array(bynderUrl).nullable().optional(),
	tags: z.array(z.string().max(100)).optional(),
	textMetaproperties: z.array(JsonObjectSchema).optional(),
	type: z.string().max(256).optional(),
	updatedAt: z.string().optional(),
	url: z.url().optional(),
	__typename: z.string().max(256)
});
const assets = Object.freeze({
	image: z.object({
		assetType: z.literal("image"),
		url: AssetUrlSchema("image"),
		bynderData: bynderDataSchema.optional()
	}),
	document: z.object({
		assetType: z.literal("document"),
		url: AssetUrlSchema("document"),
		bynderData: bynderDataSchema.optional()
	}).extend({ pageNumber: z.number().min(1).optional() }),
	video: z.object({
		assetType: z.literal("video"),
		url: AssetUrlSchema("video"),
		bynderData: bynderDataSchema.optional()
	}),
	audio: z.object({
		assetType: z.literal("audio"),
		url: AssetUrlSchema("audio"),
		bynderData: bynderDataSchema.optional()
	})
});
const _BaseAssetSchema = z.discriminatedUnion("assetType", [
	assets.image,
	assets.video,
	assets.audio,
	assets.document
]);
/**
* Normalizes the deprecated `type` field into `assetType` and strips it so it
* never appears in the parsed output type. When neither field is present the
* `fallback` type, if given, is used as the discriminator.
*/
const normalizeAssetDiscriminator = (data, fallback) => {
	if (typeof data === "object" && data !== null) {
		const { type, assetType, ...rest } = data;
		if (assetType !== void 0) return {
			assetType,
			...rest
		};
		if (typeof type === "string") return {
			assetType: type,
			...rest
		};
		if (fallback !== void 0) return {
			assetType: fallback,
			...rest
		};
	}
	return data;
};
z.preprocess((data) => normalizeAssetDiscriminator(data), _BaseAssetSchema);
function zAsset(assetTypes) {
	const selected = assetTypes.map((type) => assets[type]);
	const fallback = assetTypes.length === 1 ? assetTypes[0] : void 0;
	return z.preprocess((data) => normalizeAssetDiscriminator(data, fallback), z.discriminatedUnion("assetType", selected));
}

//#endregion
//#region src/models/method.schemas.ts
/**
* A list of all Spark methods.
*
* @see {@link MethodSlugs} only difference is that the slug "geintegreerd" is used instead of "bwgeintegreerd"
*/
const Methods = [
	"taal-lezen",
	"spelling-grammatica",
	"bwgeintegreerd",
	"engels",
	"lezen"
];
/**
* A list of all Spark methods slugs.
*
* @see {@link Methods} only difference is that "bwgeintegreerd" is used instead of the slug "geintegreerd"
*/
const MethodSlugs = [
	"taal-lezen",
	"spelling-grammatica",
	"geintegreerd",
	"engels",
	"lezen"
];
/**
* Spark method.
*
* @see {@link MethodSlugSchema} only difference is that the slug "geintegreerd" is used instead of "bwgeintegreerd"
*
* @example "taal-lezen"
* @example "spelling-grammatica"
* @example "bwgeintegreerd"
* @example "engels"
* @example "lezen"
*/
const MethodSchema = z$1.preprocess((value) => value === "geintegreerd" ? "bwgeintegreerd" : value, z$1.enum(Methods)).meta({
	id: "spark-method",
	title: "Method",
	description: "The method ",
	examples: Methods
});
/**
* Spark method slug
*
* @see {@link MethodSchema} only difference is that "bwgeintegreerd" is used instead of the slug "geintegreerd"
*
* @example "taal-lezen"
* @example "spelling-grammatica"
* @example "bwgeintegreerd"
* @example "engels"
* @example "lezen"
*/
const MethodSlugSchema = z$1.preprocess((value) => value === "bwgeintegreerd" ? "geintegreerd" : value, z$1.enum(MethodSlugs)).meta({
	id: "spark-method-slug",
	title: "Method Slug",
	description: "The method slug",
	examples: MethodSlugs
});

//#endregion
//#region src/models/markdown.schemas.ts
/**
* All markdown elements that can appear in markdown text.
*/
const MARKDOWN_ELEMENTS = [
	"h1",
	"h2",
	"h3",
	"quote",
	"ordered-list",
	"unordered-list",
	"code",
	"line",
	"link",
	"image",
	"bold",
	"italic",
	"underline",
	"paragraph"
];
/**
* Named presets that define which markdown elements are allowed.
*/
const MARKDOWN_PRESETS = {
	description: [
		"bold",
		"italic",
		"ordered-list",
		"unordered-list",
		"paragraph"
	],
	"text-decoration": [
		"bold",
		"italic",
		"paragraph"
	],
	text: [
		"bold",
		"italic",
		"underline",
		"ordered-list",
		"unordered-list",
		"paragraph"
	],
	preparation: [
		"bold",
		"italic",
		"underline",
		"ordered-list",
		"unordered-list",
		"paragraph",
		"h1",
		"link"
	]
};
const MARKDOWN_ELEMENT_PATTERNS = {
	h1: /^# .+/m,
	h2: /^## .+/m,
	h3: /^### .+/m,
	quote: /^> .+/m,
	"ordered-list": /^\d+\. .+/m,
	"unordered-list": /^- .+/m,
	code: /`[^`]*`/,
	line: /^---/m,
	link: /(?<!!)\[.+?]\(.+?\)/,
	image: /!\[.+?]\(.+?\)/,
	bold: /\*{3}.*?\*{3}|\*{2}.*?\*{2}/,
	italic: /\*{3}.*?\*{3}|(?<!\*)\*(?!\*).*?\*(?!\*)/,
	underline: /<u>.*?<\/u>/,
	paragraph: /\S[^\n]*\n{2,}\S[^\n]*/m
};
function visibleMarkdownLength(markdown) {
	return markdown.replace(/^(\d+\. |- |# )/gm, "").replace(/!?\[(.*?)]\(.*?\)/g, "$1").replace(/<\/?u>/g, "").replace(/\*{1,3}/g, "").length;
}
const visibleLengthMembers = {
	min(minimum, params) {
		return this.superRefine((data, ctx) => {
			if (visibleMarkdownLength(data) < minimum) ctx.addIssue({
				code: "too_small",
				origin: "string",
				minimum,
				inclusive: true,
				input: data,
				...typeof params === "string" ? { message: params } : params
			});
		});
	},
	max(maximum, params) {
		return this.superRefine((data, ctx) => {
			if (visibleMarkdownLength(data) > maximum) ctx.addIssue({
				code: "too_big",
				origin: "string",
				maximum,
				inclusive: true,
				input: data,
				...typeof params === "string" ? { message: params } : params
			});
		});
	},
	nonempty(params) {
		return this.min(1, params);
	}
};
const initZodString = (inst, def) => z$1.ZodString.init(inst, def);
const ZodMarkdownString = z$1.core.$constructor("ZodMarkdownString", initZodString, visibleLengthMembers);
/**
* Creates a markdown schema that only allows the specified elements.
* Use the pre-built preset schemas when possible; this factory is for custom allow-lists.
*
* @param allowed - the markdown elements permitted in the text
*/
function zMarkdown(allowed) {
	const markdownElements = [...new Set(allowed)].toSorted();
	const id = markdownElements.length > 0 ? `markdown-${markdownElements.join("-")}` : "markdown";
	const notAllowedElements = Object.entries(MARKDOWN_ELEMENT_PATTERNS).filter(([element]) => !markdownElements.includes(element));
	return z$1.core._string(ZodMarkdownString, {}).superRefine((data, ctx) => {
		const notAllowed = notAllowedElements.filter(([_, pattern]) => pattern.test(data)).map(([element]) => element);
		if (notAllowed.length > 0) ctx.addIssue({
			code: "custom",
			message: `Markdown contains disallowed elements: ${notAllowed.join(", ")}.`
		});
	}).meta({
		id,
		markdownElements
	}).brand("markdown");
}
/**
* @deprecated Use zMarkdown instead, which provides better type inference and flexibility.
*/
function markdownSchemaWithAllowed(allowed) {
	return zMarkdown(allowed);
}
/**
* Markdown text limited to bold, italic, ordered lists, unordered lists, and paragraphs.
* Suitable for short descriptive content.
*
* @example "**Bold text** and *italic text*"
* @example "- Item one\n- Item two"
* @example "First paragraph\n\nSecond paragraph"
*/
const zMarkdownDescription = zMarkdown(MARKDOWN_PRESETS.description).meta({
	name: "spark-markdown-description",
	title: "Markdown Description",
	description: "Markdown text allowing bold, italic, ordered lists, unordered lists, and paragraphs.",
	examples: [
		"**Bold text** and *italic text*",
		"- Item one\n- Item two",
		"First paragraph\n\nSecond paragraph"
	]
});
/**
* Markdown text limited to bold, italic, and paragraphs.
* Suitable for inline text decoration.
*
* @example "**Bold** and *italic*"
* @example "*Italic only*"
*/
const zMarkdownTextDecoration = zMarkdown(MARKDOWN_PRESETS["text-decoration"]).meta({
	name: "spark-markdown-text-decoration",
	title: "Markdown Text Decoration",
	description: "Markdown text allowing bold, italic, and paragraphs.",
	examples: ["**Bold** and *italic*", "*Italic only*"]
});
/**
* Markdown text allowing bold, italic, underline, ordered lists, unordered lists, and paragraphs.
* Suitable for general body text.
*
* @example "**Bold** and <u>underlined</u>"
* @example "1. First\n2. Second"
*/
const zMarkdownText = zMarkdown(MARKDOWN_PRESETS.text).meta({
	name: "spark-markdown-text",
	title: "Markdown Text",
	description: "Markdown text allowing bold, italic, underline, ordered lists, unordered lists, and paragraphs.",
	examples: ["**Bold** and <u>underlined</u>", "1. First\n2. Second"]
});
/**
* Rich markdown text allowing h1 headings, bold, italic, underline, lists, and paragraphs.
* Suitable for long-form preparation content.
*
* @example "# Heading\n\n**Bold** and *italic*"
* @example "- Item\n- Another\n\n<u>Underlined</u>"
*/
const zMarkdownPreparation = zMarkdown(MARKDOWN_PRESETS.preparation).meta({
	name: "spark-markdown-preparation",
	title: "Markdown Preparation",
	description: "Rich markdown text allowing h1 headings, bold, italic, underline, lists, and paragraphs.",
	examples: ["# Heading\n\n**Bold** and *italic*", "- Item\n- Another\n\n<u>Underlined</u>"]
});
/**
* Inline markdown limited to bold, italic, and underline.
* Suitable for subtitles, where no block elements are wanted.
*
* @example "**Bold** and <u>underlined</u>"
* @example "*Italic only*"
*/
const zMarkdownSubtitle = zMarkdown([
	"bold",
	"italic",
	"underline"
]).meta({
	name: "spark-markdown-subtitle",
	title: "Markdown Subtitle",
	description: "Inline markdown text allowing bold, italic, and underline.",
	examples: ["**Bold** and <u>underlined</u>", "*Italic only*"]
});
function zMarkdownSubtitleWithMaxLength(maxLength) {
	return zMarkdownSubtitle.min(3).max(maxLength);
}

//#endregion
//#region src/models/score.constants.ts
const MINIMUM_SCORE = 0;
const MAXIMUM_SCORE = 100;

//#endregion
//#region src/primitives/academic-year.constants.ts
/**
* The month (1-based) whose first day starts a new academic year: 1 August.
*
* Everything before that date still belongs to the previous academic year, so
* `2026-07-31` is academic year 2025 while `2026-08-01` is academic year 2026.
*/
const ACADEMIC_YEAR_START_MONTH = 8;
/**
* The earliest academic year Spark accepts. Nothing in the platform predates it,
* so a smaller value means the input (or the system clock) is wrong.
*/
const ACADEMIC_YEAR_MIN = 2010;

//#endregion
//#region src/models/title.schemas.ts
const DEFAULT_MAX_LENGTH = 256;
/**
* Marker that content authors type inside a word to add a "soft hyphen": an
* optional break point the display layer may use when the word wraps. The
* display layer converts it to a real soft hyphen (see `withSoftHyphens` in
* `@spark/slide-components`); here it is only excluded from the length count.
*/
const SOFT_HYPHEN_MARKER = "~";
/**
* Soft hyphen (U+00AD): the invisible character a {@link SOFT_HYPHEN_MARKER} is
* rendered and stored as. It stays invisible until the browser wraps a word at
* that point.
*/
const SOFT_HYPHEN = "­";
/** Hint shown in the CMS for fields that support {@link SOFT_HYPHEN_MARKER}. */
const SOFT_HYPHEN_HINT = `Gebruik ${"~"} om zelf te bepalen waar de tekst wordt afgebroken (bijv. “Wonder${"~"}lijke wereld”). Zonder ${"~"} wordt de tekst automatisch afgebroken.`;
/**
* Length of a title as seen by a reader: soft-hyphen markers do not count when
* the field supports them, since they are invisible in the rendered output.
* @param value Trimmed title value
* @param ignoreMarker Whether {@link SOFT_HYPHEN_MARKER}s should be excluded
*/
function countableLength(value, ignoreMarker) {
	return ignoreMarker ? value.replaceAll("~", "").length : value.length;
}
/**
* Content title, such as the title of a lesson tile, theme, or lesson.
*
* A required title must be between 3 and 256 characters; a whitespace-only
* value such as `"   "` trims to `""` and is rejected.
*
* Calling `.optional()` returns a lenient variant: a missing value
* (`undefined`) or a whitespace-only string is accepted and normalizes to an
* empty string `""`, while any title with content must still be at least 3
* characters. (Zod's built-in `.optional()` only handles literal `undefined`,
* so the method is overridden to also tolerate whitespace-only input.)
*
* @example "Het wereldrestaurant"
* @example "Dit is Nederland"
* @example "Reis door jezelf"
*/
const TitleSchema = createTitleSchema(DEFAULT_MAX_LENGTH, {
	name: "spark-title",
	title: "Title",
	description: "Content title, such as the title of a lesson tile, theme, or lesson.",
	examples: [
		"Het wereldrestaurant",
		"Dit is Nederland",
		"Reis door jezelf"
	]
});
/**
*
* @param length max length for the title (between 3 and 256 characters)
* @returns title schema with a custom max length (between 3 and 256 characters).
* Like {@link TitleSchema}, the required form rejects whitespace-only input
* while `.optional()` tolerates it.
*/
function titleSchemaWithMaxLength(length, options = {}) {
	if (length < 3 || length > DEFAULT_MAX_LENGTH) throw new Error(`Length must be between 3 and ${DEFAULT_MAX_LENGTH} characters.`);
	if (length === DEFAULT_MAX_LENGTH && !options.softHyphenMarker) return TitleSchema;
	return createTitleSchema(length, {
		name: `spark-title-${length}`,
		title: `Title (max ${length} characters)`,
		description: "Content title, such as the title of a lesson tile, theme, or lesson.",
		examples: [
			"Het wereldrestaurant",
			"Dit is Nederland",
			"Reis door jezelf"
		].map((example) => example.length > length ? `${example.slice(0, length - 3)}...` : example)
	}, options);
}
/**
* Builds a branded title schema whose `.optional()` is overridden to return a
* lenient variant (see {@link TitleSchema}). The required schema rejects
* whitespace-only input; the optional schema normalizes it to `""`.
* @param max Maximum length for the title
* @param meta Metadata describing the required schema
*/
function createTitleSchema(max, meta, options = {}) {
	const { softHyphenMarker = false } = options;
	const hintMeta = softHyphenMarker ? { hint: SOFT_HYPHEN_HINT } : {};
	const optional = z$1.preprocess(preprocessTitle, optionalTitleString(max, softHyphenMarker)).brand().optional().meta({
		...meta,
		...hintMeta,
		name: `${meta.name}-optional`,
		title: `${meta.title} (optional)`,
		description: `${meta.description} An empty or whitespace-only value normalizes to an empty string.`
	});
	return Object.assign(z$1.preprocess(preprocessTitle, titleString(max, softHyphenMarker)).brand().meta({
		...meta,
		...hintMeta
	}), { optional: () => optional });
}
/**
* Validates a (trimmed) title string between 3 and `max` characters. A
* whitespace-only input such as `"   "` trims to `""` and is rejected.
* @param max Maximum length for the title
*/
function titleString(max, ignoreMarker = false) {
	const trimmed = z$1.string().trim();
	if (!ignoreMarker) return trimmed.min(3).max(max);
	return trimmed.refine((value) => countableLength(value, true) >= 3, { error: "Title must be at least 3 characters long." }).refine((value) => countableLength(value, true) <= max, { error: `Title must be at most ${max} characters long.` });
}
/**
* Validates a (trimmed) title string for the optional case. An empty result is
* allowed — this lets a whitespace-only input such as `"   "` normalize to `""`
* instead of throwing. Any title with content must still be at least 3
* characters.
* @param max Maximum length for the title
*/
function optionalTitleString(max, ignoreMarker = false) {
	const trimmed = z$1.string().trim();
	return (ignoreMarker ? trimmed.refine((value) => countableLength(value, true) <= max, { error: `Title must be at most ${max} characters long.` }) : trimmed.max(max)).refine((value) => {
		const length = countableLength(value, ignoreMarker);
		return length === 0 || length >= 3;
	}, { error: "Title must be empty or at least 3 characters long." });
}
/**
* Preprocess a title value before validation.
* Non-string values are passed through unchanged; strings have their hyphens
* converted to non-breaking hyphens.
* @param val Raw input value
*/
function preprocessTitle(val) {
	return typeof val === "string" ? convertHyphens(val) : val;
}
/**
* Convert regular hyphens to non-breaking hyphens in text.
* Prevents unwanted line breaks in hyphenated words.
* @param text Input text to convert
* @returns Text with regular hyphens replaced by non-breaking hyphens (U+2011)
*/
function convertHyphens(text) {
	return text.replaceAll("-", "‑");
}

//#endregion
//#region src/primitives/slug.schemas.ts
const SLUG = /^([a-z0-9]+(-[a-z0-9]+)*)$/;
/**
* A slugified string.
* @example "example-slug"
*/
const SlugSchema = z$1.string().trim().toLowerCase().min(3).regex(SLUG).brand().meta({
	name: "slug",
	title: "Slug",
	description: "A slugified string.",
	examples: ["example-slug", "another-example"]
});

//#endregion
export { JsonNonNullSchema as A, MethodSlugSchema as C, ASSET_TYPES as D, ASSET_EXTENSIONS as E, isFrozen as F, JsonScalarSchema as M, JsonSchema as N, zAsset as O, deepFreeze as P, MethodSchema as S, Methods as T, zMarkdownPreparation as _, titleSchemaWithMaxLength as a, zMarkdownText as b, MAXIMUM_SCORE as c, MARKDOWN_ELEMENT_PATTERNS as d, MARKDOWN_PRESETS as f, zMarkdownDescription as g, zMarkdown as h, TitleSchema as i, JsonObjectSchema as j, JsonNonNullScalarSchema as k, MINIMUM_SCORE as l, visibleMarkdownLength as m, SOFT_HYPHEN as n, ACADEMIC_YEAR_MIN as o, markdownSchemaWithAllowed as p, SOFT_HYPHEN_MARKER as r, ACADEMIC_YEAR_START_MONTH as s, SlugSchema as t, MARKDOWN_ELEMENTS as u, zMarkdownSubtitle as v, MethodSlugs as w, zMarkdownTextDecoration as x, zMarkdownSubtitleWithMaxLength as y };